import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Pagination,
    Stack,
    Tooltip,
    Button,
} from '@mui/material';
import { Search, Eye, X, Plus } from 'lucide-react';
import { getAllBookings } from '../../Services/Bookings/BookingsApis';
import { useNavigate } from 'react-router-dom';
import DateFilterInput from '../../Components/Common/DateFilterInput';
import FilterSelect from '../../Components/Common/FilterSelect';
import GlobalPagination, { BOOKINGS_PAGE_SIZE_OPTIONS } from '../../Components/Common/Globalpagination';

const BOOKING_STATE_OPTIONS = [
    { value: '', label: 'All States' },
    { value: 'pending', label: 'Pending' },
    { value: 'host_accepted', label: 'Awaiting Host Confirmation' },
    { value: 'awaiting_admin_confirmation', label: 'Awaiting Admin Confirmation' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'date_change_requested', label: 'Date Change Requested' },
    { value: 'date_change_approved', label: 'Date Change Approved' },
    { value: 'date_change_rejected', label: 'Date Change Rejected' },
    { value: 'user_cancelled_date_change_request', label: 'User Cancelled Date Change Request' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rejected', label: 'Rejected' },
];

const PAYMENT_STATUS_OPTIONS = [
    { value: '', label: 'All Payments' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Failed', label: 'Failed' },
];

const BOOKING_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'direct', label: 'Direct' },
    { value: 'affiliate', label: 'Affiliate' },
];

const Bookings = () => {
    const navigate = useNavigate();

    const [filters, setFilters] = useState(() => ({
        search: '',
        bookingStatus: '',
        paymentStatus: '',
        bookingType: '',
        startDate: '',
        endDate: '',
        page: 1,
        pageSize: 10
    }));

    // Local state for debounced live typing
    const [searchTerm, setSearchTerm] = useState('');

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 10
    });

    // Handle filter changes
    const handleFilterChange = useCallback((field, value) => {
        setFilters(prev => {
            const nextFilters = {
                ...prev,
                [field]: value,
                page: field === 'page' ? value : 1
            };
            if (field === 'pageSize') {
                nextFilters.page = 1;
            }
            return nextFilters;
        });
    }, []);

    // Debounce search input (400ms) for booking ID, name, retreat
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm !== filters.search) {
                handleFilterChange('search', searchTerm);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [searchTerm, filters.search, handleFilterChange]);

    // Fetch bookings data whenever filters change
    const fetchBookings = useCallback(async (activeFilters) => {
        setLoading(true);
        setError(null);
        try {
            let response;
            let filteredBookings = [];

            if (activeFilters.bookingStatus === 'pending' || activeFilters.bookingStatus === 'host_accepted') {
                // For Pending and Awaiting Host Confirmation filters, both show bookings with bookingStatus === "pending"
                const { bookingStatus, ...filtersWithoutStatus } = activeFilters;
                response = await getAllBookings(filtersWithoutStatus);

                // Filter client-side for pending bookings only
                filteredBookings = (response.data || []).filter(
                    booking => (booking.bookingStatus || '').toLowerCase() === 'pending'
                );

                // Apply pagination to filtered results
                const pageSize = response.pageSize || 10;
                const currentPage = activeFilters.page || 1;
                const startIndex = (currentPage - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

                setBookings(paginatedBookings);
                setPagination({
                    totalRecords: filteredBookings.length,
                    totalPages: Math.ceil(filteredBookings.length / pageSize),
                    currentPage,
                    pageSize
                });
            } else if (activeFilters.bookingStatus === 'awaiting_admin_confirmation') {
                // For Awaiting Admin Confirmation, show bookings with bookingStatus === "host_accepted"
                const { bookingStatus, ...filtersWithoutStatus } = activeFilters;
                response = await getAllBookings(filtersWithoutStatus);

                // Filter client-side for host_accepted bookings
                filteredBookings = (response.data || []).filter(
                    booking => (booking.bookingStatus || '').toLowerCase() === 'host_accepted'
                );

                // Apply pagination to filtered results
                const pageSize = response.pageSize || 10;
                const currentPage = activeFilters.page || 1;
                const startIndex = (currentPage - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

                setBookings(paginatedBookings);
                setPagination({
                    totalRecords: filteredBookings.length,
                    totalPages: Math.ceil(filteredBookings.length / pageSize),
                    currentPage,
                    pageSize
                });
            } else {
                // For other filters (All States, Confirmed, Completed, Cancelled, etc.), use normal API call
                response = await getAllBookings(activeFilters);
                filteredBookings = response.data || [];

                // Apply client-side filtering for the selected status (case-insensitive)
                if (activeFilters.bookingStatus) {
                    const targetStatus = activeFilters.bookingStatus.toLowerCase();
                    filteredBookings = filteredBookings.filter(
                        booking => (booking.bookingStatus || '').toLowerCase() === targetStatus
                    );
                }

                setBookings(filteredBookings);
                setPagination({
                    totalRecords: filteredBookings.length,
                    totalPages: Math.ceil(filteredBookings.length / (response.pageSize || 10)),
                    currentPage: activeFilters.page || 1,
                    pageSize: response.pageSize || 10
                });
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch bookings');
            console.error('Error fetching bookings:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Automatically trigger fetch whenever filters change
    useEffect(() => {
        fetchBookings(filters);
    }, [filters, fetchBookings]);

    // Format date in short format (e.g., "12 Aug")
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        return `${day} ${month}`;
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate && !endDate) return 'N/A';
        const start = startDate ? formatDate(startDate) : '';
        const end = endDate ? formatDate(endDate) : '';
        if (start && end) return `${start} – ${end}`;
        return start || end;
    };

    const handleViewDetails = (booking) => {
        navigate('/bookings/view-details', { state: { booking } });
    };

    // Format booking status label
    const formatBookingStatus = (status) => {
        if (!status) return 'N/A';
        const statusMap = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'rejected': 'Rejected',
            'completed': 'Completed',
            'host_accepted': 'Host Accepted',
            'date_change_requested': 'Date Change Requested',
            'date_change_rejected': 'Date Change Rejected',
            'date_change_approved': 'Date Change Approved',
            'user_cancelled_date_change_request': 'User Cancelled Date Change',
            'awaiting_admin_confirmation': 'Awaiting Admin Confirmation'
        };
        return statusMap[status.toLowerCase()] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Get status styles
    const getStatusStyles = (status, type) => {
        const val = (status || '').toLowerCase();

        if (type === 'payment') {

            switch (val) {
                case 'completed':
                    return { bgcolor: '#E8F5E8', color: '#2E7D32' };
                case 'pending':
                    return { bgcolor: '#FFF3E0', color: '#F57C00' };
                case 'failed':
                    return { bgcolor: '#FFEBEE', color: '#C62828' };
                default:
                    return { bgcolor: '#F5F5F5', color: '#666' };
            }
        } else {
            switch (val) {
                case 'confirmed':
                    return { bgcolor: '#E3F2FD', color: '#1976D2' };
                case 'completed':
                    return { bgcolor: '#E8F5E8', color: '#2E7D32' };
                case 'pending':
                    return { bgcolor: '#FFF8E1', color: '#F57C00' };
                case 'host_accepted':
                    return { bgcolor: '#E8F5E8', color: '#2E7D32' };
                case 'awaiting_admin_confirmation':
                    return { bgcolor: '#F3E5F5', color: '#9C27B0' };
                case 'date_change_requested':
                    return { bgcolor: '#E1F5FE', color: '#0288D1' };
                case 'date_change_approved':
                    return { bgcolor: '#E0F2F1', color: '#00897B' };
                case 'date_change_rejected':
                    return { bgcolor: '#FCE4EC', color: '#D81B60' };
                case 'user_cancelled_date_change_request':
                    return { bgcolor: '#ECEFF1', color: '#546E7A' };
                case 'cancelled':
                    return { bgcolor: '#FFEBEE', color: '#C62828' };
                case 'rejected':
                    return { bgcolor: '#FFEBEE', color: '#B71C1C' };
                default:
                    return { bgcolor: '#F5F5F5', color: '#666' };
            }
        }
    };
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh' }}>
            {/* Header Section */}
            <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500, mb: 3 }}>
                Bookings
            </Typography>

            {/* Filter Bar */}
            <Box sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'center',
                mb: 4,
                p: 2.5,
                // bgcolor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '12px'
            }}>
                {/* Search Bar with Debounce */}
                <TextField
                    placeholder="Search by booking ID, name, retreat..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        flex: { xs: '1 1 100%', md: '1.4 1 240px' },
                        minWidth: { xs: '100%', sm: 220 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            height: '40px',
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#FFFFFF',
                            boxShadow: 'none',
                            '&:hover': {
                                border: '1px solid #CBD5E1',
                            },
                            '&.Mui-focused': {
                                border: '1px solid #FFBD65',
                            },
                        },
                        '& fieldset': {
                            border: '1px solid #E2E8F0',
                        },
                        '&:hover fieldset': {
                            border: '1px solid #CBD5E1',
                        },
                        '&.Mui-focused fieldset': {
                            border: '1px solid #FFBD65',
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 1 }}>
                                <Search size={18} color="#666" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        handleFilterChange('search', '');
                                    }}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 'none',
                                        bgcolor: 'transparent',
                                        p: 0.25,
                                        cursor: 'pointer',
                                        color: '#94A3B8',
                                        borderRadius: '50%',
                                        '&:hover': { color: '#803927' }
                                    }}
                                    title="Clear search"
                                >
                                    <X size={14} />
                                </Box>
                            </InputAdornment>
                        ) : null
                    }}
                />

                {/* Dropdowns */}
                <FilterSelect
                    placeholder="Booking State"
                    value={filters.bookingStatus}
                    onChange={(e) => handleFilterChange('bookingStatus', e.target.value)}
                    options={BOOKING_STATE_OPTIONS}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 140px' }, minWidth: { xs: '100%', sm: 130 } }}
                />

                <FilterSelect
                    placeholder="Payment Status"
                    value={filters.paymentStatus}
                    onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                    options={PAYMENT_STATUS_OPTIONS}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 130px' }, minWidth: { xs: '100%', sm: 125 } }}
                />

                <FilterSelect
                    placeholder="Booking Type"
                    value={filters.bookingType}
                    onChange={(e) => handleFilterChange('bookingType', e.target.value)}
                    options={BOOKING_TYPE_OPTIONS}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 120px' }, minWidth: { xs: '100%', sm: 115 } }}
                />

                {/* Date Filters with Capital Labels & MM/DD/YYYY Placeholder */}
                <DateFilterInput
                    label="START DATE"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 135px' }, minWidth: { xs: '100%', sm: 125 } }}
                />

                <DateFilterInput
                    label="END DATE"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 135px' }, minWidth: { xs: '100%', sm: 125 } }}
                />

                {/* Create Booking Button */}
                <Button
                    variant="contained"
                    startIcon={<Plus size={16} />}
                    onClick={() => navigate('/bookings/create')}
                    sx={{
                        flex: { xs: '1 1 100%', sm: '0 0 auto' },
                        minWidth: { xs: '100%', sm: 140 },
                        height: '40px',
                        bgcolor: '#FFBD65',
                        color: '#1B5E20',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        borderRadius: '8px',
                        boxShadow: 'none',
                    }}
                >
                    Create Booking
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                /* Table Section */
                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1100 }}>
                            <TableHead>
                                <TableRow sx={{ borderBottom: '2px solid #EEF2F6' }}>
                                    {['Booking ID', 'Guest', 'Retreat', 'Dates', 'Total', 'Amount Paid', 'Payment', 'Booking Status', 'Booking Type', 'Actions'].map((head) => (
                                        <TableCell key={head} align={head === 'Actions' ? 'right' : 'left'} sx={{
                                            fontWeight: 600,
                                            color: '#475569',
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.6px',
                                            py: 1.5,
                                            px: 1.5,
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {head}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bookings.length > 0 ? (
                                    bookings.map((booking) => {
                                        return (
                                            <TableRow key={booking.id} sx={{
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 },
                                            }}>
                                                <TableCell sx={{ color: '#666', fontWeight: 500, py: 1.25, px: 1.25, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                    {booking.bookingId || 'N/A'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 150 }}>
                                                    <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', fontWeight: 500, color: '#333' }} title={booking.user?.name}>
                                                        {booking.user?.name || 'N/A'}
                                                    </Typography>
                                                    {booking.guestCount && (
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', mt: 0.25 }}>
                                                            {booking.guestCount} guest{booking.guestCount > 1 ? 's' : ''}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 200 }}>
                                                    <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', fontWeight: 500, color: '#333' }} title={booking.retreat?.title}>
                                                        {booking.retreat?.title || 'N/A'}
                                                    </Typography>
                                                    {booking.retreat?.center?.name && (
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={booking.retreat.center.name}>
                                                            {booking.retreat.center.name}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                                    <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>
                                                        {formatDateRange(booking.startDate, booking.endDate)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                                    <Typography sx={{ color: '#666', fontWeight: 600, fontSize: '0.7rem' }}>
                                                        {booking.currency || 'INR'} {booking.totalAmount || '0.00'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                                    <Typography sx={{ color: '#34A853', fontWeight: 600, fontSize: '0.7rem' }}>
                                                        {booking.currency || 'INR'} {booking.amountPaid || '0.00'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25 }}>
                                                    <Box sx={{
                                                        display: 'inline-block',
                                                        px: 1.5, py: 0.3,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        ...getStatusStyles(booking.paymentStatus, 'payment')
                                                    }}>
                                                        {booking.paymentStatus ? booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1).toLowerCase() : 'N/A'}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25 }}>
                                                    <Box sx={{
                                                        display: 'inline-block',
                                                        px: 1.5, py: 0.35,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                        ...getStatusStyles(booking.bookingStatus, 'booking')
                                                    }}>
                                                        {formatBookingStatus(booking.bookingStatus)}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.25 }}>
                                                    <Box sx={{
                                                        display: 'inline-block',
                                                        px: 1.5, py: 0.3,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        textTransform: 'capitalize',
                                                        bgcolor: booking.bookingType === 'affiliate' ? '#E3F2FD' : '#F5F5F5',
                                                        color: booking.bookingType === 'affiliate' ? '#1976D2' : '#666'
                                                    }}>
                                                        {booking.bookingType || 'N/A'}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.5, px: 1.5 }}>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#666' }}
                                                            onClick={() => handleViewDetails(booking)}
                                                        >
                                                            <Eye size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 8, color: '#999', fontSize: '0.875rem' }}>
                                            No bookings found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Pagination */}
            <GlobalPagination
                page={filters.page}
                pageSize={filters.pageSize}
                totalRecords={pagination.totalRecords}
                totalPages={pagination.totalPages}
                pageSizeOptions={BOOKINGS_PAGE_SIZE_OPTIONS}
                onPageChange={(newPage) => {
                    handleFilterChange('page', newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newSize) => {
                    handleFilterChange('pageSize', newSize);
                }}
                loading={loading}
            />
        </Box>
    );
};

export default Bookings;
